import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Check, ChevronDown, MapPin, Settings, TriangleAlert } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@repo/ui/src/components/ui/drawer';
import { UNASSIGNED_BRANCH } from '../../stores/branchStore';
import { useBranchScope } from '../../hooks/useBranchScope';
import { useCan } from '../../hooks/useCan';

/**
 * The branch lens on a phone: a full-width strip immediately under the top bar.
 *
 * Same idea as the sidebar row — it sits in the app frame and wraps the content
 * below — and the same binding rule: an org with no branches gets no strip and
 * no pixel.
 *
 * A vaul Drawer, not a DropdownMenu and not a Radix Sheet: menu rows have to be
 * 44px targets under a thumb, and every phone bottom panel in this app is a
 * vaul Drawer (MoreSheet, RecordPaymentDrawer, ExpenseDrawer) — swipe-down to
 * dismiss, grab handle, one gesture language. This shipped as a Sheet first
 * and was corrected (INVARIANTS §6: phone bottom panels are vaul Drawers).
 */
export function BranchScopeStrip() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const scope = useBranchScope();
  const [open, setOpen] = useState(false);
  // MEMBERs get the switcher but not the settings page it links to (that
  // route redirects them to their profile) — the row is a dead end for them
  // otherwise (final review F3).
  const canManageSettings = useCan('manage:settings');
  if (!scope.visible) return null;

  const scoped = scope.state !== 'all';
  const pick = (next: string | null) => { scope.select(next); setOpen(false); };

  return (
    <>
      <div
        className={cn(
          'flex min-h-[44px] w-full shrink-0 items-stretch border-b md:hidden',
          scoped ? 'border-brand-200 bg-brand-500/10' : 'border-surface-200 bg-surface-50',
          scope.state === 'unassigned' && 'border-attention-border bg-attention-tint',
        )}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('branch.trigger')}
          className="flex min-w-0 flex-1 items-center gap-2 px-[9.6px] text-left"
        >
          {scope.state === 'unassigned' ? (
            <TriangleAlert className="size-4 shrink-0 text-attention-foreground" strokeWidth={1.9} />
          ) : (
            <MapPin
              className={cn('size-4 shrink-0', scope.state === 'branch' ? 'text-brand-600' : 'text-surface-500')}
              strokeWidth={scope.state === 'branch' ? 1.9 : 1.8}
            />
          )}
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-[13.5px]',
              scope.state === 'all' && 'font-medium text-surface-500',
              scope.state === 'branch' && 'font-semibold text-brand-700',
              scope.state === 'unassigned' && 'font-semibold text-attention-foreground',
            )}
          >
            {scope.label}
          </span>
          <ChevronDown
            className={cn(
              'size-[15px] shrink-0',
              scope.state === 'branch' ? 'text-brand-400' : 'text-surface-400',
            )}
            strokeWidth={1.8}
          />
        </button>

        {/* One tap back to Tất cả. Without it, escaping a scope on a phone costs
            a sheet, a scroll and a second tap. */}
        {scoped && (
          <button
            type="button"
            onClick={() => scope.select(null)}
            className={cn(
              'shrink-0 border-l px-[12.8px] text-[12.5px] font-semibold whitespace-nowrap',
              scope.state === 'unassigned'
                ? 'border-attention-border text-attention-foreground'
                : 'border-brand-200 text-brand-700',
            )}
          >
            {t('branch.clear')}
          </button>
        )}
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        {/* Content must NOT be the scroll container — vaul decides
            scroll-vs-drag by looking for a scrollable CHILD (see MoreSheet).
            The inner div owns the scrolling; the grab handle stays pinned. */}
        <DrawerContent className="max-h-[80vh] rounded-t-2xl">
          <DrawerTitle className="sr-only">{t('branch.trigger')}</DrawerTitle>
          <DrawerDescription className="sr-only">{t('branch.menuHint')}</DrawerDescription>
          <div className="min-h-0 flex-1 overflow-y-auto py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
            <StripRow active={scope.state === 'all'} onClick={() => pick(null)}>
              <span className="flex-1">{t('branch.all')}</span>
            </StripRow>

            <div className="my-1 h-px bg-surface-100" />

            {scope.branches.map((b) => (
              <StripRow key={b.id} active={scope.branchId === b.id} onClick={() => pick(b.id)}>
                <span className="min-w-0 flex-1 truncate">{b.name}</span>
                <span className="shrink-0 font-mono text-[11px] text-surface-400">{b.slotCount}</span>
              </StripRow>
            ))}

            {scope.unassignedSlotCount > 0 && (
              <>
                <div className="my-1 h-px bg-surface-100" />
                <StripRow
                  active={scope.state === 'unassigned'}
                  attention
                  onClick={() => pick(UNASSIGNED_BRANCH)}
                >
                  <span className="min-w-0 flex-1 truncate text-attention-foreground">{t('branch.unassigned')}</span>
                  <span className="flex h-[18px] shrink-0 items-center rounded-full bg-attention px-1.5 font-mono text-[10.5px] font-semibold text-attention-foreground">
                    {scope.unassignedSlotCount}
                  </span>
                </StripRow>
              </>
            )}

            {canManageSettings && (
              <>
                <div className="my-1 h-px bg-surface-100" />
                <StripRow
                  active={false}
                  onClick={() => { setOpen(false); navigate('/settings/branches'); }}
                >
                  <Settings className="size-4 shrink-0 text-surface-500" strokeWidth={1.8} />
                  <span className="flex-1 text-surface-500">{t('branch.manage')}</span>
                </StripRow>
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/** Every sheet row is a 44px target — explicit px, because outside the sidebar
 *  `h-11` renders 35.2px (INVARIANTS §6). */
function StripRow({
  active, attention, onClick, children,
}: {
  active: boolean;
  attention?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[44px] w-full items-center gap-2 px-[16px] text-left text-[14px] text-surface-800"
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        {active && (
          <Check
            className={cn('size-4', attention ? 'text-attention-foreground' : 'text-brand-500')}
            strokeWidth={3}
          />
        )}
      </span>
      {children}
    </button>
  );
}
